import { calculateCardCompletion } from "@shared/field-definitions";
import type { WizardSlice } from "./types";
import { groupTransitionMeasuresByTopic } from "./transitionMeasures.utils";
import type {
  EthicsData,
  TransparencyData,
  LeadershipData,
  AccountabilityData,
  TransitionMeasure,
} from "@/contexts/wizard/types";

/**
 * Governance slice state (all five governance cards)
 */
export interface GovernanceSliceState {
  ethicsData: EthicsData;
  transparencyData: TransparencyData;
  leadershipData: LeadershipData;
  accountabilityData: AccountabilityData;
  // Transition measures, keyed by topicId (ethics, transparency, leadership)
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
}

/**
 * API response structure for governance data
 */
export interface GovernanceApiData {
  ethicsData?: EthicsData;
  transparencyData?: TransparencyData;
  leadershipData?: LeadershipData;
  accountabilityData?: AccountabilityData;
  transitionMeasures?: TransitionMeasure[];
}

/**
 * State setters for governance slice
 */
export interface GovernanceStateSetter {
  setEthicsData: (data: EthicsData) => void;
  setTransparencyData: (data: TransparencyData) => void;
  setLeadershipData: (data: LeadershipData) => void;
  setAccountabilityData: (data: AccountabilityData) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
}

// ==================== API FUNCTIONS ====================

async function fetchGovernance(assessmentId: string): Promise<GovernanceApiData> {
  const response = await fetch(`/api/assessments/${assessmentId}/governance`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch governance data");
  return response.json();
}

async function saveGovernance(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}/governance`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

// ==================== HYDRATION MAPPER ====================

const defaultEthicsData: EthicsData = {
  hasConvictions: null,
  convictionsCount: "",
  finesAmount: "",
  finesCurrency: "usd",
  antiCorruptionPolicy: null,
  whistleblowerMechanism: null,
  evidenceFiles: [],
};

const defaultTransparencyData: TransparencyData = {
  hasFinancialStatements: null,
  financialStatementsFrequency: "annual",
  hasExternalAudit: null,
  stakeholderCommunication: "",
  supportingDocumentsLinks: "",
  supportingDocuments: [],
};

const defaultLeadershipData: LeadershipData = {
  hasGovernanceBody: null,
  femaleBoardMembers: "",
  maleBoardMembers: "",
  boardMembersTotal: "",
  boardDiversityFemalePercent: "",
};

const defaultAccountabilityData: AccountabilityData = {
  sustainabilityOwnerRole: "",
  controversialSectorsRevenue: {},
  benchmarkExclusion: null,
};

function hydrateGovernanceState(apiData: GovernanceApiData, setter: GovernanceStateSetter): void {
  setter.setEthicsData(apiData.ethicsData || defaultEthicsData);
  setter.setTransparencyData(apiData.transparencyData || defaultTransparencyData);
  setter.setLeadershipData(apiData.leadershipData || defaultLeadershipData);
  setter.setAccountabilityData(apiData.accountabilityData || defaultAccountabilityData);

  // Transition measures: flat array from the API, grouped by topicId for reducer state
  groupTransitionMeasuresByTopic(apiData.transitionMeasures || [], setter.setTransitionMeasuresForTopic);
}

// ==================== SIGNATURE & DIRTY TRACKING ====================

function computeGovernanceSignature(state: GovernanceSliceState): string {
  const payload = {
    ethicsData: state.ethicsData,
    transparencyData: state.transparencyData,
    leadershipData: state.leadershipData,
    accountabilityData: state.accountabilityData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
  };
  return JSON.stringify(payload);
}

function isGovernanceDirty(state: GovernanceSliceState, baselineSignature: string): boolean {
  const currentSignature = computeGovernanceSignature(state);
  return currentSignature !== baselineSignature;
}

// ==================== SAVE ====================

async function saveGovernanceSlice(assessmentId: string, state: GovernanceSliceState): Promise<void> {
  const payload = {
    ethicsData: state.ethicsData,
    transparencyData: state.transparencyData,
    leadershipData: state.leadershipData,
    accountabilityData: state.accountabilityData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
    ethicsCompletenessPercent: calculateCardCompletion('ethics', state.ethicsData),
    transparencyCompletenessPercent: calculateCardCompletion('transparency', state.transparencyData),
    leadershipCompletenessPercent: calculateCardCompletion('leadership', state.leadershipData),
    accountabilityCompletenessPercent: calculateCardCompletion('accountability', state.accountabilityData),
  };

  const response = await saveGovernance(assessmentId, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to save governance data" }));
    throw new Error(error.error || "Failed to save governance data");
  }
}

// ==================== COMPLETION DATA ====================

function getGovernanceCompletionData(state: GovernanceSliceState) {
  return {
    ethicsData: state.ethicsData,
    transparencyData: state.transparencyData,
    leadershipData: state.leadershipData,
    accountabilityData: state.accountabilityData,
  };
}

// ==================== SLICE EXPORT ====================

export const governanceSlice: WizardSlice<GovernanceSliceState, GovernanceApiData, GovernanceStateSetter> = {
  queryKey: (assessmentId: string) => ["governance", assessmentId],
  fetch: fetchGovernance,
  hydrate: hydrateGovernanceState,
  isDirty: isGovernanceDirty,
  computeSignature: computeGovernanceSignature,
  save: saveGovernanceSlice,
  getCompletionData: getGovernanceCompletionData,
  impactsCompletion: true,
};
